// Recently-viewed cases quick-switch — a small dropdown in the global chrome
// listing the cases you've opened most recently, so you can hop between them
// without going back to the list. Per-browser (localStorage); mirrors the
// helpdesk "Recent tickets" pattern, kept lightweight (no backend) for QC.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { QcCaseStatus } from '@itms-qc/shared';
import { StatusPill } from './badges';
import { relativeTime } from '../lib/format';

const KEY = 'itms-qc:recent-cases';
const CAP = 8;

interface RecentCase {
  id: number;
  caseNumber: string;
  title: string;
  status: QcCaseStatus;
  viewedAt: string;
}

function read(): RecentCase[] {
  try {
    const raw = localStorage.getItem(KEY);
    const arr = raw ? (JSON.parse(raw) as RecentCase[]) : [];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

/** Record that the user opened a case (most-recent first, deduped, capped). */
export function logCaseView(c: { id: number; caseNumber: string; title: string; status: QcCaseStatus }): void {
  try {
    const next: RecentCase[] = [
      { ...c, viewedAt: new Date().toISOString() },
      ...read().filter((x) => x.id !== c.id),
    ].slice(0, CAP);
    localStorage.setItem(KEY, JSON.stringify(next));
    window.dispatchEvent(new Event('itms-qc:recent-changed'));
  } catch {
    /* localStorage unavailable — recent list just won't populate */
  }
}

export function RecentCasesDropdown(): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<RecentCase[]>([]);
  const ref = useRef<HTMLDivElement | null>(null);

  // Refresh from storage when opened and whenever a view is logged.
  useEffect(() => {
    const refresh = () => setItems(read());
    refresh();
    window.addEventListener('itms-qc:recent-changed', refresh);
    return () => window.removeEventListener('itms-qc:recent-changed', refresh);
  }, []);

  useEffect(() => {
    if (!open) return;
    setItems(read());
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Exclude the case currently being viewed.
  const m = /^\/cases\/(\d+)/.exec(location.pathname);
  const currentId = m ? Number(m[1]) : null;
  const visible = items.filter((i) => i.id !== currentId);

  if (items.length === 0) return null; // nothing to show yet

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Recently viewed cases"
        aria-label="Recently viewed cases"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          background: 'rgba(255,255,255,0.14)',
          border: '1px solid rgba(255,255,255,0.25)',
          color: '#fff',
          padding: '6px 10px',
          borderRadius: 6,
          cursor: 'pointer',
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <span aria-hidden>🕘</span>
        <span>Recent</span>
        <span aria-hidden style={{ fontSize: 10 }}>▾</span>
      </button>
      {open && (
        <div role="menu" style={panel}>
          <div style={header}>Recently viewed</div>
          {visible.length === 0 && <div style={{ padding: 12, fontSize: 12, color: '#6b7280' }}>No other recent cases.</div>}
          {visible.map((i) => (
            <button
              key={i.id}
              type="button"
              onClick={() => {
                setOpen(false);
                navigate(`/cases/${i.id}`);
              }}
              style={row}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#6b7280' }}>{i.caseNumber}</span>
                <StatusPill status={i.status} />
                <span style={{ marginLeft: 'auto', fontSize: 11, color: '#9ca3af' }}>{relativeTime(i.viewedAt)}</span>
              </div>
              <div style={{ fontSize: 13, color: '#1A202C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {i.title}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const panel: CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 320,
  maxWidth: '90vw',
  background: '#fff',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  boxShadow: '0 10px 30px rgba(15,23,42,0.18)',
  zIndex: 300,
  maxHeight: 440,
  overflowY: 'auto',
};
const header: CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid #eef0f3',
  fontSize: 11,
  fontWeight: 700,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};
const row: CSSProperties = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  padding: '10px 12px',
  border: 'none',
  borderBottom: '1px solid #f3f4f6',
  background: 'transparent',
  cursor: 'pointer',
};
